import { TransactionItem as KitTransactionItem } from "lola-framework-ui-test";
import { useTranslation } from "react-i18next";
import { useStore } from "../store/globalState";
import {
  CheckCircleIcon,
  ErrorCircleIcon,
  MoreCircleIcon,
  WarningCircleIcon,
} from "../assets/icons";
import {
  TransactionsStatus,
  type TransactionsStatus as Status,
} from "../utils/commons";

export interface TransactionItemProps {
  operationId: string;
  status: Status;
  amount: string;
  currency: string;
  date: string;
  onClick?: (operationId: string) => void;
}

const buildStatusField = (status: string) => {
  switch (status) {
    case "completed":
      return { icon: <CheckCircleIcon />, value: status, color: "#31C859" };
    case "failed":
    case "error":
      return { icon: <ErrorCircleIcon />, value: status, color: "#FF3B30" };
    case "processing":
      return { icon: <MoreCircleIcon />, value: status, color: "#A3A3A3" };
    case "cancelled":
      return {
        icon: <ErrorCircleIcon bgColor="#A3A3A3" />,
        value: status,
        color: "#A3A3A3",
      };
    default:
      return { icon: <WarningCircleIcon />, value: status, color: "#F28D19" };
  }
};

const resolveStatusLabel = (status: Status): string => {
  switch (status) {
    case TransactionsStatus.COMPLETED:
      return TransactionsStatus.COMPLETED;
    case TransactionsStatus.CANCELED:
    case TransactionsStatus.CANCELLED:
      return TransactionsStatus.CANCELLED;
    default:
      return status;
  }
};

export const TransactionItem: React.FC<TransactionItemProps> = ({
  operationId,
  status,
  amount,
  currency,
  date,
  onClick,
}) => {
  const { theme } = useStore();
  const { title, bodyCopy } = theme.dataView;
  const { t } = useTranslation();

  const label = resolveStatusLabel(status);
  const statusField = buildStatusField(label.toLocaleLowerCase());

  return (
    <KitTransactionItem
      operationId={operationId}
      statusInfo={{
        icon: statusField.icon,
        value: t(
          `receiptSummary.fields.status.items.${statusField.value}`
        ),
        color: statusField.color,
      }}
      amount={amount}
      currency={currency}
      date={date}
      onClick={onClick}
      translations={{
        avatar: t("transactions.avatar"),
        transfers: t("transactions.transfers"),
        statusTranslations: {},
      }}
      textColor={title}
      alternativeTextColor={bodyCopy}
    />
  );
};
